import { markCogoExtensionSeen, markCogoSignedIn } from './cogoExtensionPrefs'

const CHANNEL = 'menu-cogo-extension'

/**
 * Exclusive ownership of the Collect&Go tab.
 * Enrich, sync, and “Meer artikelen” must not interleave navigations — that
 * cross-wires zoeken/PDP scrapes into the wrong product’s candidates.
 */
let cogoTabLockChain: Promise<unknown> = Promise.resolve()
let cogoTabLockDepth = 0
let cogoTabLockReason: string | null = null

export function isCogoTabBusy() {
  return cogoTabLockDepth > 0
}

export function cogoTabLockOwner(): string | null {
  return cogoTabLockReason
}

/**
 * Run `fn` with exclusive tab ownership. Callers queue FIFO; the busy flag is
 * set for the whole critical section (not a TOCTOU check-then-set).
 */
export function withCogoTabLock<T>(
  reason: string,
  fn: () => Promise<T>
): Promise<T> {
  const run = cogoTabLockChain.then(async () => {
    cogoTabLockDepth++
    cogoTabLockReason = reason
    try {
      return await fn()
    } finally {
      cogoTabLockDepth = Math.max(0, cogoTabLockDepth - 1)
      cogoTabLockReason = cogoTabLockDepth > 0 ? cogoTabLockReason : null
    }
  })
  // Keep the queue alive even when a holder rejects.
  cogoTabLockChain = run.then(
    () => undefined,
    () => undefined
  )
  return run
}

const CART_URL = 'https://www.collectandgo.be/nl/winkelwagen'
export const COGO_SEARCH_URL = (term: string) =>
  `https://www.collectandgo.be/nl/zoek?searchTerm=${encodeURIComponent(term)}`

export type CogoExtensionStatus = {
  installed: boolean
  cogoTabCount: number
  version: string | null
  loggingIn?: boolean
}

export type CogoAuth = {
  loggedIn: boolean
  blocked: boolean
  loggingIn?: boolean
  url?: string
  cartCount?: number | null
  status?: string
  message?: string
}

export type CogoCmdResult = {
  ok?: boolean
  status?: string
  message?: string
  loggedIn?: boolean
  blocked?: boolean
  url?: string
  cartCount?: number | null
  tabId?: number
  version?: string
  cogoTabCount?: number
  [key: string]: unknown
}

type CmdResultMessage = {
  channel: string
  type: 'cmdResult'
  requestId: string
  ok: boolean
  result: CogoCmdResult
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms))
}

export function cogoCommand(
  cmd: Record<string, unknown>,
  timeoutMs = 60_000
): Promise<CogoCmdResult> {
  if (typeof window === 'undefined') {
    return Promise.resolve({
      ok: false,
      status: 'error',
      message: 'Geen window',
    })
  }

  return new Promise((resolve) => {
    const requestId = crypto.randomUUID()

    function onMessage(event: MessageEvent) {
      if (event.source !== window) return
      const data = event.data as CmdResultMessage | null
      if (
        !data ||
        data.channel !== CHANNEL ||
        data.type !== 'cmdResult' ||
        data.requestId !== requestId
      ) {
        return
      }
      cleanup()
      resolve(data.result ?? { ok: false, status: 'error', message: 'Leeg' })
    }

    function cleanup() {
      window.removeEventListener('message', onMessage)
      clearTimeout(timer)
    }

    window.addEventListener('message', onMessage)
    window.postMessage({ channel: CHANNEL, type: 'cmd', requestId, cmd }, '*')
    const timer = setTimeout(() => {
      cleanup()
      resolve({
        ok: false,
        status: 'timeout',
        message: 'Extensie reageert niet — herlaad de pagina of herinstalleer',
      })
    }, timeoutMs)
  })
}

export async function probeCogoExtension(
  timeoutMs = 400
): Promise<CogoExtensionStatus> {
  return probeCogoExtensionFast(timeoutMs)
}

/** Probe that fails fast when content script is absent. */
export async function probeCogoExtensionFast(
  timeoutMs = 400
): Promise<CogoExtensionStatus> {
  return new Promise((resolve) => {
    const requestId = crypto.randomUUID()
    function onMessage(event: MessageEvent) {
      if (event.source !== window) return
      const data = event.data as CmdResultMessage | null
      if (
        !data ||
        data.channel !== CHANNEL ||
        data.type !== 'cmdResult' ||
        data.requestId !== requestId
      ) {
        return
      }
      cleanup()
      const r = data.result
      const version = r?.version != null ? String(r.version) : null
      const installed = Boolean(r?.version != null || r?.ok)
      if (installed) markCogoExtensionSeen(version)
      resolve({
        installed,
        cogoTabCount: Number(r?.cogoTabCount) || 0,
        version,
        loggingIn: Boolean(r?.loggingIn),
      })
    }
    function cleanup() {
      window.removeEventListener('message', onMessage)
      clearTimeout(timer)
    }
    window.addEventListener('message', onMessage)
    window.postMessage(
      {
        channel: CHANNEL,
        type: 'cmd',
        requestId,
        cmd: { type: 'getStatus' },
      },
      '*'
    )
    const timer = setTimeout(() => {
      cleanup()
      resolve({
        installed: false,
        cogoTabCount: 0,
        version: null,
        loggingIn: false,
      })
    }, timeoutMs)
  })
}

export async function cogoEnsureTab(opts?: { active?: boolean; url?: string }) {
  return cogoCommand({
    type: 'ensureTab',
    active: opts?.active ?? false,
    url: opts?.url ?? CART_URL,
  })
}

export async function cogoFocusTab() {
  return cogoCommand({ type: 'focusTab' })
}

export async function cogoNavigate(url: string, active = false) {
  return cogoCommand({ type: 'navigate', url, active })
}

/** Close Menu-owned automation tab (never a user-opened Collect&Go tab). */
export async function cogoCloseTab() {
  return cogoCommand({ type: 'closeTab' }, 15_000)
}

/**
 * Show center countdown on the Cogo tab, then close if Menu owns it.
 * Resolves `{ cancelled: true }` when the user keeps the tab open.
 */
export async function cogoShowCloseCountdown(seconds = 10) {
  return cogoCommand(
    { type: 'showCloseCountdown', seconds },
    (seconds + 5) * 1000
  )
}

export async function cogoHideCloseCountdown() {
  return cogoCommand({ type: 'hideCloseCountdown' }, 5_000)
}

export async function cogoCheckAuth(): Promise<CogoAuth> {
  const r = await cogoCommand({ type: 'checkAuth' })
  if (r.status === 'no_tab') {
    return {
      loggedIn: false,
      blocked: false,
      loggingIn: false,
      status: 'no_tab',
      message: r.message,
    }
  }
  // Extension/tab messaging failed — not a real logged-out signal.
  if (r.status === 'error' || r.status === 'timeout') {
    return {
      loggedIn: false,
      blocked: false,
      loggingIn: false,
      status: r.status,
      message: r.message,
    }
  }
  const auth: CogoAuth = {
    loggedIn: Boolean(r.loggedIn),
    blocked: Boolean(r.blocked),
    loggingIn: Boolean(r.loggingIn) || r.status === 'logging_in',
    url: r.url,
    cartCount: r.cartCount ?? null,
    status: r.status,
    message: r.message,
  }
  if (auth.loggedIn) markCogoSignedIn()
  return auth
}

export async function cogoShowLoginNotice() {
  return cogoCommand({ type: 'showLoginNotice' })
}

export async function cogoHideLoginNotice() {
  return cogoCommand({ type: 'hideLoginNotice' })
}

export async function cogoUpdateSyncHud(payload: Record<string, unknown>) {
  return cogoCommand({ type: 'updateSyncHud', payload }, 8_000)
}

export async function cogoHideSyncHud() {
  return cogoCommand({ type: 'hideSyncHud' }, 5_000)
}

export async function cogoTryAdd(
  quantity: number,
  opts?: {
    quantityUnit?: 'st' | 'kg'
    unitType?: 'P' | 'W' | 'PA' | null
  }
) {
  return cogoCommand({
    type: 'tryAdd',
    quantity,
    quantityUnit: opts?.quantityUnit ?? 'st',
    unitType: opts?.unitType ?? null,
  })
}

export async function cogoVerifyInCart(
  name: string,
  opts?: { title?: string; assortmentUrl?: string }
) {
  return cogoCommand({
    type: 'verifyInCart',
    name,
    title: opts?.title,
    assortmentUrl: opts?.assortmentUrl,
  })
}

export async function cogoScrapeSearchResults() {
  return cogoCommand({ type: 'scrapeSearchResults' })
}

export async function cogoScrapeFavorites() {
  return cogoCommand({ type: 'scrapeFavorites' })
}

export async function cogoScrapeProductDetail() {
  return cogoCommand({ type: 'scrapeProductDetail' })
}

export async function cogoScrapeCart() {
  return cogoCommand({ type: 'scrapeCart' })
}

export async function cogoRemoveCartItem(opts: {
  name?: string
  title?: string
  assortmentUrl?: string
  slug?: string
}) {
  return cogoCommand({ type: 'removeCartItem', ...opts })
}

export async function cogoClearCart() {
  return cogoCommand({ type: 'clearCart' })
}
